const apiProxyUrl = String(process.env.VUE_APP_API_PROXY_URL || '').trim().replace(/\/+$/, '');
const apiBackendUrl = String(process.env.VUE_APP_PLATFORMA_API_BACKEND_URL || '').trim().replace(/\/+$/, '');

const DEFAULT_PERMISSIONS = Object.freeze({
  account: Object.freeze({
    settings: false,
    billing: false,
    notifications: false,
    support: false,
  }),
  main: Object.freeze({
    dashboard: false,
    members: false,
    mediaContent: false,
    analytics: false,
    payments: false,
  }),
  engage: Object.freeze({
    chat: false,
    stream: false,
    post: false,
    broadcast: false,
  }),
  platform: Object.freeze({
    themeEditor: false,
    productInstances: false,
    pageEditor: false,
    memberSettings: false,
    memberSettingsDomains: false,
    memberSettingsPayments: false,
    memberSettingsCrypto: false,
  }),
  coreFeatures: Object.freeze({}),
});

export const STOCKROOM_TABS = Object.freeze([
  'orders',
  'products',
  'collections',
  'shipping',
  'checkout',
  'communications',
  'payments',
  'discounts',
]);

let sessionCache = null;
let sessionCacheAt = 0;
const SESSION_CACHE_TTL_MS = 15000;

const asBool = (value) => {
  if (value === true) return true;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes';
  }
  return false;
};

const toObject = (value) => (value && typeof value === 'object' ? value : {});

export const normalizeStaffPermissions = (input) => {
  const source = toObject(input);
  return {
    account: {
      settings: asBool(toObject(source.account).settings),
      billing: asBool(toObject(source.account).billing),
      notifications: asBool(toObject(source.account).notifications),
      support: asBool(toObject(source.account).support),
    },
    main: {
      dashboard: asBool(toObject(source.main).dashboard),
      members: asBool(toObject(source.main).members),
      mediaContent: asBool(toObject(source.main).mediaContent),
      analytics: asBool(toObject(source.main).analytics),
      payments: asBool(toObject(source.main).payments),
    },
    engage: {
      chat: asBool(toObject(source.engage).chat),
      stream: asBool(toObject(source.engage).stream),
      post: asBool(toObject(source.engage).post),
      broadcast: asBool(toObject(source.engage).broadcast),
    },
    platform: {
      themeEditor: asBool(toObject(source.platform).themeEditor),
      productInstances: asBool(toObject(source.platform).productInstances),
      pageEditor: asBool(toObject(source.platform).pageEditor),
      memberSettings: asBool(toObject(source.platform).memberSettings),
      memberSettingsDomains: asBool(toObject(source.platform).memberSettingsDomains),
      memberSettingsPayments: asBool(toObject(source.platform).memberSettingsPayments),
      memberSettingsCrypto: asBool(toObject(source.platform).memberSettingsCrypto),
    },
    coreFeatures: toObject(source.coreFeatures),
  };
};

export const buildAdminStaffSession = () => ({
  success: true,
  isStaff: false,
  role: 'admin',
  permissions: DEFAULT_PERMISSIONS,
  canManageStaff: true,
  staff: null,
  creator: null,
});

export const buildLockedStaffSession = () => ({
  success: false,
  isStaff: true,
  role: 'staff',
  permissions: DEFAULT_PERMISSIONS,
  canManageStaff: false,
  staff: null,
  creator: null,
});

export const hasPermissionPath = (permissions, permissionPath) => {
  if (!permissionPath) return true;
  const normalized = normalizeStaffPermissions(permissions || {});
  const normalizedPath = String(permissionPath || '').trim();
  if (normalizedPath.startsWith('platform.memberSettings') && !asBool(normalized?.platform?.memberSettings)) {
    return false;
  }
  const parts = String(permissionPath)
    .split('.')
    .map((part) => part.trim())
    .filter(Boolean);
  if (!parts.length) return true;

  let node = normalized;
  for (const part of parts) {
    if (!node || typeof node !== 'object' || !(part in node)) {
      return false;
    }
    node = node[part];
  }
  return asBool(node);
};

const normalizeCoreFeaturePermissions = (staffSession) => {
  const coreFeatures = staffSession?.permissions?.coreFeatures;
  if (!coreFeatures || typeof coreFeatures !== 'object') return {};
  return coreFeatures;
};

const getEnabledCoreFeatureKeys = (staffSession) =>
  Object.keys(normalizeCoreFeaturePermissions(staffSession)).filter((key) =>
    asBool(normalizeCoreFeaturePermissions(staffSession)[key])
  );

const getEnabledRootCoreFeatureKeys = (staffSession) =>
  getEnabledCoreFeatureKeys(staffSession).filter((key) => !String(key || '').includes('::stockroom.'));

const buildStockroomPermissionKey = (featureSlug, tab) => `${featureSlug}::stockroom.${tab}`;

export const getAllowedStockroomTabsForFeature = (staffSession, featureSlugValue) => {
  const featureSlug = String(featureSlugValue || '').trim().toLowerCase();
  if (!featureSlug) return [];
  if (!staffSession?.isStaff || String(staffSession?.role || '').toLowerCase() === 'admin') {
    return [...STOCKROOM_TABS];
  }
  if (!hasPermissionPath(staffSession.permissions || {}, 'platform.productInstances')) {
    return [];
  }

  const coreFeatures = normalizeCoreFeaturePermissions(staffSession);
  const enabledCoreKeys = getEnabledCoreFeatureKeys(staffSession);
  if (!enabledCoreKeys.length) {
    return [...STOCKROOM_TABS];
  }

  const hasAnyTabScopedRule = STOCKROOM_TABS.some((tab) =>
    Object.prototype.hasOwnProperty.call(coreFeatures, buildStockroomPermissionKey(featureSlug, tab))
  );

  if (!hasAnyTabScopedRule) {
    return [];
  }
  const enabledChildren = STOCKROOM_TABS.filter((tab) =>
    asBool(coreFeatures[buildStockroomPermissionKey(featureSlug, tab)])
  );
  if (enabledChildren.length > 0) return enabledChildren;
  return asBool(coreFeatures[featureSlug]) ? [...STOCKROOM_TABS] : [];
};

export const hasStockroomTabPermission = (staffSession, featureSlugValue, tabValue) => {
  const tab = String(tabValue || '').trim().toLowerCase();
  if (!STOCKROOM_TABS.includes(tab)) return false;
  const allowed = getAllowedStockroomTabsForFeature(staffSession, featureSlugValue);
  return allowed.includes(tab);
};

const withApiBase = (pathValue) => {
  const normalizedPath = String(pathValue || '').startsWith('/')
    ? String(pathValue || '')
    : `/${String(pathValue || '')}`;
  return apiProxyUrl ? `${apiProxyUrl}${normalizedPath}` : normalizedPath;
};

const withBackendBase = (pathValue) => {
  const normalizedPath = String(pathValue || '').startsWith('/')
    ? String(pathValue || '')
    : `/${String(pathValue || '')}`;
  return apiBackendUrl ? `${apiBackendUrl}${normalizedPath}` : normalizedPath;
};

const readPayload = async (response) => {
  const contentType = String(response.headers.get('content-type') || '').toLowerCase();
  if (contentType.includes('application/json')) {
    return response.json().catch(() => ({}));
  }
  const rawText = await response.text().catch(() => '');
  return { __rawText: rawText };
};

export const fetchCreatorStaffSession = async ({ force = false } = {}) => {
  const now = Date.now();
  if (!force && sessionCache && now - sessionCacheAt < SESSION_CACHE_TTL_MS) {
    return sessionCache;
  }

  const fallback = buildAdminStaffSession();
  const lockedFallback = buildLockedStaffSession();
  try {
    const sessionPathPairs = [
      { proxyPath: '/api/creator-staff/session', backendPath: '/api/creators/staff/session' },
      { proxyPath: '/api/creators/staff/session', backendPath: '/api/creators/staff/session' },
    ];
    const candidateUrls = [];
    const pushCandidate = (urlValue) => {
      const value = String(urlValue || '').trim();
      if (!value || candidateUrls.includes(value)) return;
      candidateUrls.push(value);
    };
    for (const pair of sessionPathPairs) {
      const proxyPath = String(pair?.proxyPath || '').trim();
      const backendPath = String(pair?.backendPath || proxyPath).trim();
      pushCandidate(withApiBase(proxyPath));
      pushCandidate(proxyPath);
      pushCandidate(withBackendBase(backendPath));
      pushCandidate(backendPath);
    }

    let payload = null;
    for (const url of candidateUrls) {
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
      });
      const parsedPayload = await readPayload(response);
      const rawText = String(parsedPayload?.__rawText || '').toLowerCase();
      const looksLikeHtmlRouteMiss = rawText.includes('<!doctype html') && rawText.includes('cannot get');

      if ((!response.ok || parsedPayload?.success === false) && looksLikeHtmlRouteMiss) {
        continue;
      }
      if (response.status === 403) {
        sessionCache = lockedFallback;
        sessionCacheAt = now;
        return lockedFallback;
      }
      if (!response.ok || parsedPayload?.success === false) {
        continue;
      }
      payload = parsedPayload;
      break;
    }

    if (!payload) {
      sessionCache = fallback;
      sessionCacheAt = now;
      return fallback;
    }

    const isStaff = Boolean(payload?.isStaff);
    const role = String(payload?.role || '').toLowerCase() === 'admin' ? 'admin' : (isStaff ? 'staff' : 'admin');
    const normalized = {
      ...payload,
      isStaff,
      role,
      permissions: normalizeStaffPermissions(payload?.permissions || {}),
      canManageStaff: Boolean(payload?.canManageStaff),
    };

    sessionCache = normalized;
    sessionCacheAt = now;
    return normalized;
  } catch (_error) {
    sessionCache = fallback;
    sessionCacheAt = now;
    return fallback;
  }
};

export const resetCreatorStaffSessionCache = () => {
  sessionCache = null;
  sessionCacheAt = 0;
};

export const getCreatorRequiredPermissionForPath = (pathValue) => {
  const path = String(pathValue || '').toLowerCase().trim();
  if (!path.startsWith('/creator')) return null;

  if (path.startsWith('/creator/stockroom/')) {
    const withoutPrefix = path.substring('/creator/stockroom/'.length);
    const parts = withoutPrefix.split('/').filter(Boolean);
    const featureSlug = String(parts[0] || '').trim().toLowerCase();
    const tab = String(parts[1] || 'orders').trim().toLowerCase();
    if (!featureSlug) return 'platform.productInstances';
    return `__stockroom_route__:${featureSlug}:${tab}`;
  }

  if (path.startsWith('/creator/account/user-permissions')) return '__staff_management__';
  if (path.startsWith('/creator/account/billing')) return 'account.billing';
  if (path.startsWith('/creator/account/security')) return '__self_security__';
  if (path.startsWith('/creator/account')) return 'account.settings';
  if (path.startsWith('/creator/notifications')) return 'account.notifications';
  if (path.startsWith('/creator/support')) return 'account.support';

  if (path.startsWith('/creator/dashboard')) return 'main.dashboard';
  if (path.startsWith('/creator/users')) return 'main.members';
  if (path.startsWith('/creator/content-drive')) return 'main.mediaContent';
  if (path.startsWith('/creator/analytics')) return 'main.analytics';
  if (path.startsWith('/creator/cart')) return 'main.payments';
  if (path.startsWith('/creator/payments')) return 'main.payments';
  if (path.startsWith('/creator/advertising')) return 'main.payments';

  if (path.startsWith('/creator/chat')) return 'engage.chat';
  if (path.startsWith('/creator/stream')) return 'engage.stream';
  if (path.startsWith('/creator/meet')) return 'engage.stream';
  if (path.startsWith('/creator/newsfeed')) return 'engage.post';
  if (path.startsWith('/creator/broadcast')) return 'engage.broadcast';

  if (path.startsWith('/creator/themes')) return 'platform.themeEditor';
  if (path.startsWith('/creator/theme-editor/editor-member-settings') || path.startsWith('/creator/theme-editor/editor-member-billing')) {
    return 'platform.memberSettings';
  }
  if (path === '/creator/theme-editor' || path.startsWith('/creator/theme-editor?')) {
    return 'platform.themeEditor';
  }
  if (path.startsWith('/creator/theme-editor/')) {
    const withoutPrefix = path.substring('/creator/theme-editor/'.length);
    const featureSlug = withoutPrefix.split('/').filter(Boolean)[0] || '';
    if (!featureSlug || featureSlug === 'editor-member-settings' || featureSlug === 'editor-member-billing') {
      return 'platform.themeEditor';
    }
    return `__theme_editor_instance__:${featureSlug}`;
  }
  if (path.startsWith('/creator/editor-feature-select')) return 'platform.productInstances';
  if (path.startsWith('/creator/page-editor')) return 'platform.pageEditor';
  if (path.startsWith('/creator/site-page-editor')) return 'platform.pageEditor';

  if (path.startsWith('/creator/app-settings/domains')) return 'platform.memberSettingsDomains';
  if (path.startsWith('/creator/app-settings/payments')) return 'platform.memberSettingsPayments';
  if (path.startsWith('/creator/app-settings/integrations')) return 'platform.memberSettingsPayments';
  if (path.startsWith('/creator/app-settings/plan-tiers')) return 'platform.memberSettingsPayments';
  if (path.startsWith('/creator/app-settings/crypto')) return 'platform.memberSettingsCrypto';
  if (path.startsWith('/creator/app-settings')) return 'platform.memberSettings';

  return null;
};

export const canAccessCreatorPath = (staffSession, pathValue) => {
  const session = staffSession || buildAdminStaffSession();
  if (!session.isStaff) return true;
  if (String(session.role || '').toLowerCase() === 'admin') return true;

  const requiredPermission = getCreatorRequiredPermissionForPath(pathValue);
  if (!requiredPermission) return true;
  if (requiredPermission === '__self_security__') return true;
  if (requiredPermission === '__staff_management__') return Boolean(session.canManageStaff);
  if (requiredPermission === 'platform.themeEditor') {
    return (
      hasPermissionPath(session.permissions || {}, 'platform.themeEditor') &&
      hasPermissionPath(session.permissions || {}, 'platform.productInstances') &&
      getEnabledRootCoreFeatureKeys(session).length > 0
    );
  }
  if (String(requiredPermission).startsWith('__stockroom_route__:')) {
    const [, featureSlug = '', tab = 'orders'] = String(requiredPermission).split(':');
    return hasStockroomTabPermission(session, featureSlug, tab);
  }
  if (String(requiredPermission).startsWith('__theme_editor_instance__:')) {
    const featureSlug = String(requiredPermission).replace('__theme_editor_instance__:', '').trim().toLowerCase();
    const themeEditorAllowed = hasPermissionPath(session.permissions || {}, 'platform.themeEditor');
    const platformAccess = hasPermissionPath(session.permissions || {}, 'platform.productInstances');
    if (!platformAccess || !themeEditorAllowed) return false;

    const coreFeatures = (session.permissions && typeof session.permissions === 'object' && session.permissions.coreFeatures && typeof session.permissions.coreFeatures === 'object')
      ? session.permissions.coreFeatures
      : {};
    const coreFeatureKeys = Object.keys(coreFeatures);
    const enabledCoreKeys = coreFeatureKeys.filter((key) => asBool(coreFeatures[key]));
    if (!enabledCoreKeys.length) {
      // No per-instance restrictions configured; allow all product instances.
      return true;
    }
    return enabledCoreKeys.includes(featureSlug);
  }
  return hasPermissionPath(session.permissions || {}, requiredPermission);
};

export const getFirstAccessibleCreatorPath = (staffSession) => {
  if (staffSession?.isStaff && String(staffSession?.role || '').toLowerCase() !== 'admin') {
    const coreFeatures = normalizeCoreFeaturePermissions(staffSession);
    const stockroomSlugs = new Set();
    for (const key of Object.keys(coreFeatures || {})) {
      if (!asBool(coreFeatures[key])) continue;
      const token = String(key || '').trim().toLowerCase();
      if (!token) continue;
      if (token.includes('::stockroom.')) {
        stockroomSlugs.add(token.split('::stockroom.')[0]);
        continue;
      }
      if (token.startsWith('platform.')) continue;
      stockroomSlugs.add(token);
    }
    for (const slug of stockroomSlugs) {
      const allowedTabs = getAllowedStockroomTabsForFeature(staffSession, slug);
      if (!allowedTabs.length) continue;
      const preferredTab = allowedTabs.includes('orders') ? 'orders' : allowedTabs[0];
      const stockroomPath = `/creator/stockroom/${encodeURIComponent(slug)}/${preferredTab}`;
      if (canAccessCreatorPath(staffSession, stockroomPath)) {
        return stockroomPath;
      }
    }
  }

  const candidates = [
    '/creator/dashboard',
    '/creator/users',
    '/creator/content-drive',
    '/creator/analytics',
    '/creator/cart/products',
    '/creator/payments',
    '/creator/chat',
    '/creator/stream',
    '/creator/meet',
    '/creator/newsfeed',
    '/creator/broadcast',
    '/creator/theme-editor',
    '/creator/page-editor',
    '/creator/app-settings/domains',
    '/creator/account/general',
    '/creator/account/security',
    '/creator/support',
  ];

  for (const path of candidates) {
    if (canAccessCreatorPath(staffSession, path)) return path;
  }
  return '/creator/account/general';
};
